import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import BentoCard from '../ui/BentoCard'
import MaterialIcon from '../ui/MaterialIcon'
import SessionPhaseStepper from '../workout/SessionPhaseStepper'
import { KILO } from '../../constants/KILO_THEME'
import { coolDownSteps, type CoolDownProtocol } from '../../lib/coolDownProtocol'

type CoolDownPhaseProps = {
  protocol: CoolDownProtocol
  saving?: boolean
  error?: string
  onComplete: () => void
  onBack: () => void
}

const CoolDownPhase = ({ protocol, saving, error, onComplete, onBack }: CoolDownPhaseProps) => {
  const { t } = useTranslation()
  const steps = useMemo(() => coolDownSteps(protocol), [protocol])
  const [stepIndex, setStepIndex] = useState(0)
  const step = steps[stepIndex]
  const isLast = stepIndex >= steps.length - 1

  if (!step) return null

  return (
    <div className={`flex flex-col gap-3 pb-24 ${KILO.bg.page}`}>
      <header>
        <p className={KILO.meta}>{t('coach.sections.workout')}</p>
        <h1 className={`mt-1 text-2xl ${KILO.heading}`}>{t('workout.cooldown.title')}</h1>
        <p className={`mt-1 text-sm ${KILO.text.soft}`}>
          {t('workout.cooldown.stepOf', { current: stepIndex + 1, total: steps.length })}
        </p>
      </header>

      <SessionPhaseStepper currentPhase="cooldown" />

      <div className="h-1.5 overflow-hidden rounded-full bg-kilo-nested">
        <div
          className="h-full rounded-full bg-kilo-accent transition-all duration-500"
          style={{ width: `${Math.round(((stepIndex + 1) / steps.length) * 100)}%` }}
        />
      </div>

      <BentoCard gradient="slate" featured>
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className={KILO.meta}>
              {step.kind === 'cardio' ? t('workout.cooldown.cardioFlush') : t('workout.cooldown.stretch')}
            </p>
            <h2 className="mt-1 text-xl font-bold tracking-tight text-kilo-text">{step.title}</h2>
          </div>
          <MaterialIcon name={step.kind === 'cardio' ? 'directions_run' : 'self_improvement'} size="md" />
        </div>
        <p className={`mt-4 text-sm leading-relaxed ${KILO.text.muted}`}>{step.instruction}</p>
        <p className="mt-4 flex items-center gap-2 font-mono text-xs text-kilo-accent">
          <MaterialIcon name="schedule" size="sm" />
          {step.duration}
        </p>
      </BentoCard>

      {isLast ? (
        <button
          type="button"
          disabled={saving}
          className="group flex h-12 items-center justify-center gap-2 rounded-2xl bg-kilo-accent text-sm font-bold text-black disabled:opacity-50"
          onClick={onComplete}
        >
          <MaterialIcon name="done_outline" filled size="sm" />
          {saving ? t('app.profile.saving') : t('workout.cooldown.complete')}
        </button>
      ) : (
        <button
          type="button"
          className="group flex h-12 items-center justify-center gap-2 rounded-2xl bg-kilo-accent text-sm font-bold text-black"
          onClick={() => setStepIndex((index) => index + 1)}
        >
          <MaterialIcon name="arrow_forward" size="sm" />
          {t('workout.cooldown.nextStep')}
        </button>
      )}

      {stepIndex > 0 ? (
        <button
          type="button"
          className="group flex h-12 items-center justify-center gap-2 rounded-2xl border border-kilo-border bg-kilo-card text-sm font-semibold text-kilo-muted"
          onClick={() => setStepIndex((index) => index - 1)}
        >
          <MaterialIcon name="arrow_back" hoverable size="sm" />
          {t('workout.cooldown.previousStep')}
        </button>
      ) : null}

      <button
        type="button"
        className="group flex h-12 items-center justify-center gap-2 rounded-2xl border border-kilo-border bg-kilo-card text-sm font-semibold text-kilo-muted"
        onClick={onBack}
      >
        <MaterialIcon name="arrow_back" hoverable size="sm" />
        {t('workout.cooldown.backToTraining')}
      </button>

      {error ? <p className="text-sm text-red-400">{error}</p> : null}
    </div>
  )
}

export default CoolDownPhase
